/**
 * @file src/utils/githubRoadmap.ts
 * @fileoverview Shared GitHub roadmap fetcher with lightweight caching.
 */
import { getEnv } from './env';

export interface GithubRoadmapIssue {
  title: string;
  labels?: string[];
  milestone?: string;
  state?: string;
  number?: number;
  html_url?: string;
  updated_at?: string;
  closed_at?: string | null;
  comments?: number;
  comment_count?: number;
  comments_count?: number;
  commentCount?: number;
  comments_total?: number;
  commentsTotal?: number;
  comments_count_total?: number;
  comments_data?: unknown;
}

export interface GithubRoadmapResponse {
  issues?: GithubRoadmapIssue[];
  milestones?: Array<{ title: string }>;
}

export const isOpenHighPriorityIssue = (issue: GithubRoadmapIssue): boolean => {
  const labels = Array.isArray(issue.labels) ? issue.labels : [];
  return issue.state === 'open'
    && labels.some(label => label.toLowerCase() === 'priority-high');
};

const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
let cachedData: GithubRoadmapResponse | null = null;
let cachedAt = 0;
let inflight: Promise<GithubRoadmapResponse> | null = null;

interface FetchGithubRoadmapOptions {
  forceRefresh?: boolean;
}

export const fetchGithubRoadmap = async (
  options: FetchGithubRoadmapOptions = {}
): Promise<GithubRoadmapResponse> => {
  const { forceRefresh = false } = options;
  const now = Date.now();

  if (forceRefresh) {
    cachedData = null;
    cachedAt = 0;
  }

  if (!forceRefresh && cachedData && now - cachedAt < CACHE_TTL_MS) {
    return cachedData;
  }

  if (inflight && !forceRefresh) {
    return inflight;
  }

  inflight = (async () => {
    try {
      const apiUrl = getEnv('VITE_AI_API_URL');
      if (!apiUrl) {
        throw new Error('AI API URL ontbreekt.');
      }

      const response = await fetch(`${apiUrl}/github/roadmap`, {
        headers: {
          'x-api-key': getEnv('VITE_AI_API_KEY') ?? ''
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as GithubRoadmapResponse;
      cachedData = data;
      cachedAt = Date.now();
      return data;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};
